/**
 * Auth token cookie management.
 *
 * Primitive cookie helpers live in `./utils/cookies` — this module only wraps
 * them with the access/refresh token business rules (names, expiries, secure
 * flags) and re-exports the primitives so that callers importing from
 * `@/shared/lib` keep working.
 */

import { deleteCookie, getCookie, setCookie } from './utils/cookies'

export { deleteCookie, getCookie, setCookie } from './utils/cookies'
export type { CookieOptions } from './utils/cookies'

/**
 * Set auth tokens in cookies
 */
export function setAuthTokens(accessToken: string, refreshToken: string): void {
  // Only use secure cookies in production (HTTPS)
  // On localhost HTTP, secure cookies won't be accessible
  const isSecure = window.location.protocol === 'https:'

  // Set access token with 24 hour expiry
  const accessExpiry = new Date()
  accessExpiry.setHours(accessExpiry.getHours() + 24)

  setCookie('access_token', accessToken, {
    expires: accessExpiry,
    path: '/',
    secure: isSecure,
    sameSite: isSecure ? 'strict' : 'lax',
  })

  // Set refresh token with longer expiry (e.g., 7 days)
  const refreshExpiry = new Date()
  refreshExpiry.setDate(refreshExpiry.getDate() + 7)

  setCookie('refresh_token', refreshToken, {
    expires: refreshExpiry,
    path: '/',
    secure: isSecure,
    sameSite: isSecure ? 'strict' : 'lax',
  })
}

/**
 * Get auth tokens from cookies
 */
export function getAuthTokens(): {
  accessToken: string | null
  refreshToken: string | null
} {
  return {
    accessToken: getCookie('access_token'),
    refreshToken: getCookie('refresh_token'),
  }
}

/**
 * Clear auth tokens from cookies
 */
export function clearAuthTokens(): void {
  deleteCookie('access_token', { path: '/' })
  deleteCookie('refresh_token', { path: '/' })
}

/**
 * Check if user is authenticated
 * Returns true if access token exists OR if refresh token exists (can be refreshed)
 */
export function isAuthenticated(): boolean {
  const { accessToken, refreshToken } = getAuthTokens()
  return !!accessToken || !!refreshToken
}
